import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import styles from "./CategorySliders.module.css";

interface CategorySlidersProps {
  content: {
    level3Categories?: {
      name: string;
      url: string;
      level4Categories: {
        name: string;
        image: string | null;
        url: string;
      }[];
    }[];
    level4Categories?: {
      name: string;
      image: string | null;
      url: string;
    }[];
  };
}

const CategorySliders: React.FC<CategorySlidersProps> = ({ content }) => {
  // Create refs for each slider to control scrolling
  const sliderRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [sliderHasOverflow, setSliderHasOverflow] = useState<boolean[]>([]);
  const [scrollPosition, setScrollPosition] = useState<number[]>([]);
  const [maxScroll, setMaxScroll] = useState<number[]>([]);

  // Check for overflow on mount and window resize
  useEffect(() => {
    const checkForOverflow = () => {
      const overflowArray: boolean[] = [];
      const scrollPositionArray: number[] = [];
      const maxScrollArray: number[] = [];

      sliderRefs.current.forEach((slider, index) => {
        if (slider) {
          const hasOverflow = slider.scrollWidth > slider.clientWidth;
          overflowArray[index] = hasOverflow;
          scrollPositionArray[index] = slider.scrollLeft;
          maxScrollArray[index] = slider.scrollWidth - slider.clientWidth;
        }
      });

      setSliderHasOverflow(overflowArray);
      setScrollPosition(scrollPositionArray);
      setMaxScroll(maxScrollArray);
    };

    // Check on initial render
    checkForOverflow();

    // Add resize listener
    window.addEventListener("resize", checkForOverflow);
    return () => {
      window.removeEventListener("resize", checkForOverflow);
    };
  }, [content]);

  // Update scroll position when scrolling
  const handleSliderScroll = (index: number) => {
    const slider = sliderRefs.current[index];
    if (slider) {
      setScrollPosition((prev) => {
        const newPositions = [...prev];
        newPositions[index] = slider.scrollLeft;
        return newPositions;
      });
    }
  };

  const handleScroll = (sliderIndex: number, direction: "left" | "right") => {
    const slider = sliderRefs.current[sliderIndex];
    if (!slider) return;

    const scrollAmount = 240; // Adjust based on item width + gap
    const scrollLeft =
      direction === "left"
        ? slider.scrollLeft - scrollAmount
        : slider.scrollLeft + scrollAmount;

    slider.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });
  };

  // Check if we're dealing with level3Categories (Level 2) or level4Categories (Level 3)
  if (content.level3Categories) {
    return (
      <div className={styles.categorySliders}>
        {content.level3Categories.map((level3Category, index) => (
          <div key={index} className={styles.sliderSection}>
            <div className={styles.sliderHeader}>
              <h2 className={styles.sliderTitle}>{level3Category.name}</h2>
              <Link
                to={`/c/${level3Category.url}`}
                className={styles.viewAllLink}
              >
                Ver todo
              </Link>
            </div>

            <div className={styles.sliderContainer}>
              {sliderHasOverflow[index] && scrollPosition[index] > 0 && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => handleScroll(index, "left")}
                  aria-label="Scroll left"
                >
                  <NavigateBeforeIcon />
                </button>
              )}

              <div
                className={styles.level4Categories}
                ref={(el) => (sliderRefs.current[index] = el)}
                onScroll={() => handleSliderScroll(index)}
              >
                {level3Category.level4Categories.map((level4Category, idx) => (
                  <Link
                    key={idx}
                    to={`/c/${level4Category.url}`}
                    className={styles.level4Category}
                  >
                    <div className={styles.circleImage}>
                      {level4Category.image ? (
                        <img
                          src={level4Category.image}
                          alt={level4Category.name}
                          className={styles.categoryImage}
                          loading="lazy"
                        />
                      ) : (
                        <div className={styles.placeholder}></div>
                      )}
                    </div>
                    <p className={styles.level4Name}>{level4Category.name}</p>
                  </Link>
                ))}
              </div>

              {sliderHasOverflow[index] &&
                scrollPosition[index] < maxScroll[index] && (
                  <button
                    className={`${styles.scrollButton} ${styles.scrollRight}`}
                    onClick={() => handleScroll(index, "right")}
                    aria-label="Scroll right"
                  >
                    <NavigateNextIcon />
                  </button>
                )}
            </div>
          </div>
        ))}
      </div>
    );
  } else if (content.level4Categories) {
    return (
      <div className={styles.categorySliders}>
        <div className={styles.sliderSection}>
          <div className={styles.sliderContainer}>
            {sliderHasOverflow[0] && scrollPosition[0] > 0 && (
              <button
                className={`${styles.scrollButton} ${styles.scrollLeft}`}
                onClick={() => handleScroll(0, "left")}
                aria-label="Scroll left"
              >
                <NavigateBeforeIcon />
              </button>
            )}

            <div
              className={styles.level4Categories}
              ref={(el) => (sliderRefs.current[0] = el)}
              onScroll={() => handleSliderScroll(0)}
            >
              {content.level4Categories.map((level4Category, idx) => (
                <Link
                  key={idx}
                  to={`/c/${level4Category.url}`}
                  className={styles.level4Category}
                >
                  <div className={styles.circleImage}>
                    {level4Category.image ? (
                      <img
                        src={level4Category.image}
                        alt={level4Category.name}
                        className={styles.categoryImage}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles.placeholder}></div>
                    )}
                  </div>
                  <p className={styles.level4Name}>{level4Category.name}</p>
                </Link>
              ))}
            </div>

            {sliderHasOverflow[0] && scrollPosition[0] < maxScroll[0] && (
              <button
                className={`${styles.scrollButton} ${styles.scrollRight}`}
                onClick={() => handleScroll(0, "right")}
                aria-label="Scroll right"
              >
                <NavigateNextIcon />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CategorySliders;
